import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { Row, Col, Button, Card } from "reactstrap";
import CreateAPLPeriodModal from "../../components/Modal/CreateAPLPeriodModal";
import APLMoments from "../../components/APL/APLMoments";
import SupervisorInfoModal from "../../components/Modal/SupervisorInfoModal";
import APLWorkCardTab from "./APLWorkCardTab";
import APLTabNavSelector from "../../components/Teacher/Tabs/APLTabNavSelector";
import APLRatings from "./APLRatings";
import APLDocumentsAndSupervisors from "./APLDocumentsAndSupervisors";
import {
  GetSupervisors,
  GetWorkCards,
  GetPeriods,
  GetEvents,
} from "../../actions/teacherQueries";
import Pacmanloader from "../../components/Pacmanloader/Pacmanloader";
import DataLoadError from "../../components/DataLoadError/DataLoadError";
import { FormattedMessage } from "react-intl";
import APLInformationPersonInfoModal from "../Modal/APLInformationPersonInfoModal";
import RemoveAndChangeDateApl from "./RemoveAndChangeDateApl";
import useAPLModalStates from "./hooks/useAPLModalStates";
import useAPLPeriodData from "./hooks/useAPLPeriodData";
import useAPLWorkCardData from "./hooks/useAPLWorkCardData";
import useAPLEvaluationQuestions from "./hooks/useAPLEvaluationQuestions";
import useAPLWorkStepMutations from "./hooks/useAPLWorkStepMutations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";
import AplExportModal from "../Modal/AplExportModal";
import EditSupervisorsModal from "../Modal/EditSupervisorsModal";
import APLPresence from "./APLPresence";

const APL = ({
  level,
  students,
  objId,
  schoolId,
  programId,
  teacherInfo,
}) => {
  const intl = useIntl();
  //Modals
  const {
    supervisorInfoOpen,
    aplPeriodOpen,
    infoPersonOpen,
    addNewWorkStepOpen,
    addNewWorkStepComponentOpen,
    exportModalOpen,
    toggleSupervisorInfo,
    toggleCreatePeriod,
    toggleInfoPersonOpen,
    toggleAddNewWorkStepModal,
    toggleAddNewWorkStepComponentModal,
    toggleExportModal,
    setAddNewWorkStepComponentOpen,
  } = useAPLModalStates();

  const periodsData = GetPeriods(level, objId);

  //Period data hook
  const {
    period,
    setPeriod,
    activePeriodId,
    periodsToSelect,
    periodGroups,
    currentPeriods,
    setCurrentPeriods,
    selectedAplTab,
  } = useAPLPeriodData(periodsData, level, objId);

  //Evaluation questions hook
  const {
    studentQuestions,
    setStudentQuestions,
    supervisorQuestions,
    setSupervisorQuestions,
    questionsToDelete,
    setQuestionsToDelete,
    handleSaveEvaluationQuestions,
    evaluationQuestionsData,
  } = useAPLEvaluationQuestions(activePeriodId);

  //Work step mutations hook
  const {
    promtWorkComponentForDelete,
    setPromptComponentForDelete,
    workcardItemForEditName,
    setWorkcardItemForEditName,
    handleDeleteItem,
    handleChangeNameOnWorkcardItem,
    handleSetWorkcardItemForEditName,
  } = useAPLWorkStepMutations(intl, level, activePeriodId, objId);

  //Objects
  const [supervisor, setSupervisor] = useState(null);
  const [aplInfoPerson, setAplInfoPerson] = useState(null);

  //Navigation
  const [aplActiveTab, setAPLActiveTab] = useState(
    selectedAplTab || (activePeriodId !== 0 ? 1 : 2)
  );
  const [ratingLevel, setRatingLevel] = useState("student");
  const [levelSupervisor, setLevelSupervisor] = useState("supervisor");
  const [editSupervisorsOpen, setEditSupervisorsOpen] = useState(false);

  // Sync aplActiveTab when selectedAplTab changes
  useEffect(() => {
    if (selectedAplTab) {
      setAPLActiveTab(selectedAplTab);
    }
  }, [selectedAplTab]);

  const eventsData = GetEvents(level, activePeriodId, objId);

  // Refetch events when navigating to the moments tab
  useEffect(() => {
    if (aplActiveTab === 3 && eventsData.refetch) {
      eventsData.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aplActiveTab]);

  //Work card data hook
  const {
    studentParticipation: rawStudentParticipation,
    totalWorkHoursRegistered,
    currentWorkStepForAddComponent,
    columns,
    columnExtensions,
  } = useAPLWorkCardData(
    level,
    activePeriodId,
    objId,
    setPromptComponentForDelete,
    setWorkcardItemForEditName,
    setAddNewWorkStepComponentOpen
  );

  const studentParticipation = useMemo(() => {
    if (!rawStudentParticipation) return null;
    return rawStudentParticipation.map((row) => {
      const mapped = { ...row };
      if (row.edit && typeof row.edit === "object" && row.edit.onDelete) {
        mapped.edit = (
          <>
            <Button
              color="danger"
              outline={true}
              size="sm"
              onClick={row.edit.onDelete}
            >
              <FontAwesomeIcon icon="trash" />
            </Button>
            <Button
              className="ms-2"
              color="primary"
              outline={true}
              size="sm"
              onClick={row.edit.onEdit}
            >
              <FontAwesomeIcon icon="edit" />
            </Button>
          </>
        );
      }
      if (row.name && typeof row.name === "object" && row.name.onAddComponent) {
        mapped.name = (
          <Button outline color="primary" onClick={row.name.onAddComponent}>
            <FormattedMessage id="AddWorkStepComponent" />
          </Button>
        );
      }
      return mapped;
    });
  }, [rawStudentParticipation]);

  const filteredStudentsForPresence = useMemo(() => {
    if (!period?.period?.students) return [];
    return period.period.students.filter((st) =>
      students.some((studentInLevel) => studentInLevel.id === st.student.id)
    );
  }, [period?.period?.students, students]);

  const supervisors = GetSupervisors(schoolId);
  const workCards = GetWorkCards();

  if (
    supervisors.isPending ||
    workCards.isPending ||
    !currentPeriods ||
    eventsData.isPending
  ) {
    return <Pacmanloader />;
  }
  const currentProgram = teacherInfo.data.programmes.find(
    (program) => program.id === Number(programId),
  );
  if (!currentProgram) {
    return <DataLoadError />;
  }
  const programTeachers = [];
  currentProgram.classes.forEach((cl) => {
    cl.teachers.forEach((teacher) => {
      if (!programTeachers.some((t) => t.id === teacher.id)) {
        programTeachers.push(teacher);
      }
    });
  });

  const mainPanelContent = (tabIndex) => {
    switch (tabIndex) {
      case 1:
        return (
          <Col lg={12}>
            <Card body className="border-0">
              <APLPresence
                students={filteredStudentsForPresence}
                objId={objId}
                level={level}
                activePeriodId={activePeriodId}
                periodStart={period.period.from}
                periodEnd={period.period.to}
              />
            </Card>
          </Col>
        );
      case 2:
        return (
          <APLWorkCardTab
            period={period}
            studentParticipation={studentParticipation}
            totalWorkHoursRegistered={totalWorkHoursRegistered}
            columns={columns}
            columnExtensions={columnExtensions}
            promtWorkComponentForDelete={promtWorkComponentForDelete}
            setPromptComponentForDelete={setPromptComponentForDelete}
            workcardItemForEditName={workcardItemForEditName}
            setWorkcardItemForEditName={setWorkcardItemForEditName}
            handleDeleteItem={handleDeleteItem}
            handleChangeNameOnWorkcardItem={handleChangeNameOnWorkcardItem}
            handleSetWorkcardItemForEditName={handleSetWorkcardItemForEditName}
            addNewWorkStepOpen={addNewWorkStepOpen}
            toggleAddNewWorkStepModal={toggleAddNewWorkStepModal}
            addNewWorkStepComponentOpen={addNewWorkStepComponentOpen}
            toggleAddNewWorkStepComponentModal={toggleAddNewWorkStepComponentModal}
            currentWorkStepForAddComponent={currentWorkStepForAddComponent}
            level={level}
            objId={objId}
            intl={intl}
          />
        );
      case 3:
        return (
          <ColWithoutScroll
            lg={9}
            className="d-sm-flex d-lg-flex"
            style={{ margin: "auto" }}
          >
            <APLMoments
              period={period.period}
              studentEvents={eventsData}
              students={students}
              level={level}
              periodId={period.period.id}
              objectId={objId}
            />
          </ColWithoutScroll>
        );
      case 4:
        return (
          <>
            <RemoveAndChangeDateApl
              currentStartDate={period.period.from}
              currentEndDate={period.period.to}
              periodId={period.period.id}
              periodName={period.period.name}
              level={level}
              objId={objId}
              setPeriod={setPeriod}
            />
            {students.length > 0 && (
              <APLDocumentsAndSupervisors
                allSupervisors={supervisors.data}
                setLevelSupervisor={setLevelSupervisor}
                levelSupervisor={levelSupervisor}
                setSupervisor={setSupervisor}
                toggleSupervisorInfo={toggleSupervisorInfo}
                period={period}
                setPeriod={setPeriod}
                toggleInfoPersonOpen={toggleInfoPersonOpen}
                setAplInfoPerson={setAplInfoPerson}
                level={level}
                objId={objId}
                students={students}
                schoolId={schoolId}
                programTeachers={programTeachers}
                programId={programId}
                teacherInfo={teacherInfo}
              />
            )}
            <APLRatings
              loading={evaluationQuestionsData.isPending}
              ratingLevel={ratingLevel}
              setRatingLevel={setRatingLevel}
              handleSaveEvaluationQuestions={handleSaveEvaluationQuestions}
              activePeriodId={activePeriodId}
              studentQuestions={studentQuestions}
              setStudentQuestions={setStudentQuestions}
              supervisorQuestions={supervisorQuestions}
              setSupervisorQuestions={setSupervisorQuestions}
              questionsToDelete={questionsToDelete}
              setQuestionsToDelete={setQuestionsToDelete}
              level={level}
              objId={objId}
            />
          </>
        );
      default:
        return;
    }
  };

  return (
    <div className="apl--container">
      <APLTabNavSelector
        setAPLActiveTab={setAPLActiveTab}
        activeTab={aplActiveTab}
        toggleCreatePeriod={toggleCreatePeriod}
        toggleExportModal={toggleExportModal}
        toggleEditSupervisors={() => setEditSupervisorsOpen(true)}
        periodsToSelect={periodsToSelect}
        periodGroups={periodGroups}
        period={period}
      />
      <DivNoScroll className="pt-3">
        <HeaderRowNoScroll>
          {period && (
            <ColWithoutScroll
              lg={12}
              sm={12}
              style={{ minHeight: "63vh", overflow: "visible" }}
            >
              <HeaderRowNoScroll className="pt-2">
                <ColWithoutScroll lg={12}>
                  {mainPanelContent(aplActiveTab)}
                </ColWithoutScroll>
              </HeaderRowNoScroll>
            </ColWithoutScroll>
          )}
        </HeaderRowNoScroll>
        <CreateAPLPeriodModal
          students={students}
          isOpen={aplPeriodOpen}
          toggle={toggleCreatePeriod}
          supervisors={supervisors.data}
          existingWorkCards={workCards.data}
          currentPeriods={currentPeriods}
          setCurrentPeriods={setCurrentPeriods}
          handleSaveEvaluationQuestions={handleSaveEvaluationQuestions}
          studentQuestions={studentQuestions}
          setStudentQuestions={setStudentQuestions}
          supervisorQuestions={supervisorQuestions}
          setSupervisorQuestions={setSupervisorQuestions}
          questionsToDelete={questionsToDelete}
          setQuestionsToDelete={setQuestionsToDelete}
          schoolId={schoolId}
          level={level}
          objId={objId}
          programTeachers={programTeachers}
          teacherInfo={teacherInfo}
        />
        {supervisor && (
          <SupervisorInfoModal
            isOpen={supervisorInfoOpen}
            toggle={toggleSupervisorInfo}
            supervisor={supervisor}
          />
        )}
        {aplInfoPerson && (
          <APLInformationPersonInfoModal
            aplInfoPerson={aplInfoPerson}
            toggleInfoPersonOpen={toggleInfoPersonOpen}
            infoPersonOpen={infoPersonOpen}
          />
        )}
        {period && (
          <AplExportModal
            isOpen={exportModalOpen}
            toggle={toggleExportModal}
            periodId={activePeriodId}
            periodStudents={period.period.students}
            contextStudents={students}
          />
        )}
        {editSupervisorsOpen && (
          <EditSupervisorsModal
            isOpen={editSupervisorsOpen}
            toggle={() => setEditSupervisorsOpen(false)}
            supervisors={supervisors.data}
          />
        )}
      </DivNoScroll>
    </div>
  );
};
export default APL;

const ColWithoutScroll = styled(Col)``;

const DivNoScroll = styled.div`
  &::-webkit-scrollbar {
    display: none;
  }
`;

const HeaderRowNoScroll = styled(Row)`
  &::-webkit-scrollbar {
    display: none;
  }
`;
